import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { SkillCategoryUpdateResponse } from '../../../../models';
import { CareerRoleAdminActionsService } from './career-role-admin-actions.service';

export interface UpdateSkillCategoryDialogData {
  skillId: string;
  currentCategory: string;
}

@Component({
  selector: 'app-update-skill-category-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule
  ],
  template: `
    <h2 mat-dialog-title>Edit skill category</h2>
    <mat-progress-bar *ngIf="saving" mode="indeterminate"></mat-progress-bar>
    <form [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Category</mat-label>
          <input matInput formControlName="category" />
        </mat-form-field>
        <p class="hint">Updates the skill catalog for all roles using this skill.</p>
        <p class="error" *ngIf="error">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || saving">Save</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .full-width {
        width: 100%;
      }
      .hint {
        margin: 0;
        color: rgba(0, 0, 0, 0.6);
        font-size: 0.875rem;
      }
      .error {
        color: #b00020;
        margin-top: 0.75rem;
      }
    `
  ]
})
export class UpdateSkillCategoryDialogComponent {
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly dialogRef = inject(MatDialogRef<UpdateSkillCategoryDialogComponent, SkillCategoryUpdateResponse | undefined>);
  private readonly data = inject<UpdateSkillCategoryDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  saving = false;
  error = '';

  readonly form = this.fb.nonNullable.group({
    category: [this.data.currentCategory, [Validators.required, Validators.minLength(1)]]
  });

  close(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.saving) {
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      const category = this.form.controls.category.value.trim();
      const result = await firstValueFrom(this.api.patchSkillCategory(this.data.skillId, { category }));
      this.dialogRef.close(result);
    } catch (err) {
      this.error = this.actions.errorMessage(err);
    } finally {
      this.saving = false;
    }
  }
}
